import React, { useEffect } from 'react';
import type { BrandPackageColors, BrandPackageFonts } from '../../api/modules/brandPackages';
import { loadGoogleFont } from '../../utils/fontLoader';
import type { GlobalPalette } from '../../utils/deriveComponentColors';

interface BrandPackagePreviewProps {
  colors: BrandPackageColors;
  fonts: BrandPackageFonts;
  /** Optional: pass the raw palette to show all 6 colors explicitly */
  palette?: GlobalPalette;
  size?: 'small' | 'medium' | 'large';
  className?: string;
}

/**
 * UI-generated preview of a brand package
 * Shows a mini representation of how the brand package colors and fonts look
 */
export const BrandPackagePreview: React.FC<BrandPackagePreviewProps> = ({
  colors,
  fonts,
  palette,
  size = 'medium',
  className = '',
}) => {
  // Load fonts for preview
  useEffect(() => {
    if (fonts.headingFont) {
      loadGoogleFont(fonts.headingFont);
    }
    if (fonts.bodyFont && fonts.bodyFont !== fonts.headingFont) {
      loadGoogleFont(fonts.bodyFont);
    }
  }, [fonts.headingFont, fonts.bodyFont]);

  // Size configurations
  const sizeConfig = {
    small: {
      container: 'w-full',
      navbarHeight: 'h-3',
      footerHeight: 'h-3',
      headingSize: 'text-[10px]',
      bodySize: 'text-[8px]',
      buttonSize: 'text-[7px] px-1.5 py-0.5',
      badgeSize: 'text-[6px] px-1 py-0.5',
      padding: 'p-1.5',
      gap: 'gap-0.5',
      cardPadding: 'p-1',
      starSize: 'w-1.5 h-1.5',
    },
    medium: {
      container: 'w-full',
      navbarHeight: 'h-4',
      footerHeight: 'h-4',
      headingSize: 'text-xs',
      bodySize: 'text-[10px]',
      buttonSize: 'text-[8px] px-2 py-0.5',
      badgeSize: 'text-[7px] px-1.5 py-0.5',
      padding: 'p-2',
      gap: 'gap-1',
      cardPadding: 'p-1.5',
      starSize: 'w-2 h-2',
    },
    large: {
      container: 'w-full',
      navbarHeight: 'h-5',
      footerHeight: 'h-5',
      headingSize: 'text-sm',
      bodySize: 'text-xs',
      buttonSize: 'text-[10px] px-2.5 py-1',
      badgeSize: 'text-[8px] px-2 py-0.5',
      padding: 'p-3',
      gap: 'gap-1.5',
      cardPadding: 'p-2',
      starSize: 'w-2.5 h-2.5',
    },
  };

  const config = sizeConfig[size];

  // Extract palette colors if provided, otherwise derive from component colors
  const primary = palette?.primary || colors.hero?.buttonBg || '#3b82f6';
  const background = palette?.background || colors.hero?.background || '#ffffff';
  const text = palette?.text || colors.hero?.text || '#111827';
  const accent = palette?.accent || colors.testimonials?.starColor || '#f59e0b';
  const dark = palette?.dark || colors.navbar?.background || '#1f2937';

  // Determine contrasting text for dark areas
  const darkText = '#ffffff';

  // Get derived component colors with fallbacks
  const navbarBg = colors.navbar?.background || dark;
  const navbarText = colors.navbar?.text || darkText;
  const heroBg = colors.hero?.background || background;
  const heroText = colors.hero?.text || text;
  const featuresBg = colors.features?.background || background;
  const cardBg = colors.features?.cardBg || '#ffffff';
  const footerBg = colors.footer?.background || dark;
  const footerText = colors.footer?.text || darkText;

  // Check if hero background is a gradient
  const isGradient = typeof heroBg === 'string' && heroBg.includes('gradient');

  return (
    <div
      className={`rounded-lg overflow-hidden border border-gray-200 shadow-sm flex flex-col ${config.container} ${className}`}
    >
      {/* Navbar - Shows DARK color */}
      <div
        className={`${config.navbarHeight} flex items-center justify-between px-2 shrink-0`}
        style={{ backgroundColor: navbarBg }}
      >
        <div className="w-6 h-1 rounded-full opacity-90" style={{ backgroundColor: navbarText }} />
        <div className="flex gap-1 items-center">
          <div
            className="w-3 h-0.5 rounded-full opacity-60"
            style={{ backgroundColor: navbarText }}
          />
          <div
            className="w-3 h-0.5 rounded-full opacity-60"
            style={{ backgroundColor: navbarText }}
          />
          {/* Link hover preview - PRIMARY */}
          <div className="w-3 h-0.5 rounded-full" style={{ backgroundColor: primary }} />
        </div>
      </div>

      {/* Hero section - Shows BACKGROUND, TEXT, PRIMARY, ACCENT */}
      <div
        className={`${config.padding} flex flex-col ${config.gap} flex-1 min-h-0`}
        style={{
          background: isGradient ? heroBg : undefined,
          backgroundColor: !isGradient ? heroBg : undefined,
        }}
      >
        {/* Badge - ACCENT color */}
        <div
          className={`${config.badgeSize} rounded-full self-start font-medium`}
          style={{
            backgroundColor: accent,
            color: '#ffffff',
          }}
        >
          New
        </div>

        {/* Heading - TEXT color */}
        <div
          className={`${config.headingSize} font-bold leading-tight`}
          style={{
            fontFamily: fonts.headingFont
              ? `"${fonts.headingFont}", system-ui, sans-serif`
              : 'system-ui, sans-serif',
            color: heroText,
          }}
        >
          Welcome
        </div>

        {/* Body text - TEXT color (muted) */}
        <div
          className={`${config.bodySize} opacity-70 leading-tight`}
          style={{
            fontFamily: fonts.bodyFont
              ? `"${fonts.bodyFont}", system-ui, sans-serif`
              : 'system-ui, sans-serif',
            color: heroText,
          }}
        >
          Your tagline here
        </div>

        {/* Buttons row - PRIMARY color */}
        <div className="flex gap-1 mt-auto">
          <div
            className={`${config.buttonSize} rounded font-medium`}
            style={{
              backgroundColor: primary,
              color: '#ffffff',
            }}
          >
            Get Started
          </div>
          <div
            className={`${config.buttonSize} rounded font-medium border`}
            style={{
              borderColor: heroText,
              color: heroText,
              opacity: 0.7,
            }}
          >
            Learn
          </div>
        </div>
      </div>

      {/* Features row - Shows cards with PRIMARY icons and ACCENT stars */}
      <div
        className={`${config.padding} flex ${config.gap} shrink-0`}
        style={{ backgroundColor: featuresBg }}
      >
        {[1, 2, 3].map(i => (
          <div
            key={i}
            className={`flex-1 ${config.cardPadding} rounded border flex flex-col items-center`}
            style={{
              backgroundColor: cardBg,
              borderColor: `${text}15`,
            }}
          >
            {/* Icon - PRIMARY color */}
            <div
              className={`${config.starSize} rounded-sm mb-0.5`}
              style={{ backgroundColor: primary }}
            />
            {/* Star rating - ACCENT color */}
            <div className="flex gap-px">
              {[1, 2, 3].map(star => (
                <div
                  key={star}
                  className="w-1 h-1 rounded-full"
                  style={{ backgroundColor: accent }}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Footer - Shows DARK color */}
      <div
        className={`${config.footerHeight} flex items-center justify-center px-2 shrink-0`}
        style={{ backgroundColor: footerBg }}
      >
        <div className="flex gap-2 items-center">
          <div
            className="w-4 h-0.5 rounded-full opacity-50"
            style={{ backgroundColor: footerText }}
          />
          {/* Link hover - PRIMARY */}
          <div
            className="w-4 h-0.5 rounded-full"
            style={{ backgroundColor: primary, opacity: 0.8 }}
          />
        </div>
      </div>
    </div>
  );
};

export default BrandPackagePreview;
